import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Gift, X } from "lucide-react";
import { api, getToken } from "../api";
import { localApi } from "../config";
import { useAuth } from "../auth";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

// Modal to pay for a locked paid series from the viewer's wallet.
// Uses the app server's own wallet + purchase endpoints so access syncs
// between the website and the mobile app.
export const PaywallModal = ({ series, access, onClose, onOwned }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (user) api.get("/wallet").then((w) => setBalance(w.balance)).catch(() => {});
  }, [user]);

  const insufficient = balance !== null && balance < access.price - 1e-9;

  const buy = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/series/${series.id}/purchase`, {});
      onOwned && onOwned();
    } catch (e) {
      if (/not enough|insufficient/i.test(e.message)) {
        api.get("/wallet").then((w) => setBalance(w.balance)).catch(() => {});
        setErr("insufficient");
      } else {
        setErr(e.message || "Purchase failed — no money was taken.");
      }
    }
    setBusy(false);
  };

  const deposit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/wallet/deposits", { amount: access.price });
      if (r.url) window.location.href = r.url;
    } catch (e) {
      setErr(e.message || "Could not start the deposit.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="paywall-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><Lock size={20} /></span>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="paywall-close"><X size={18} /></button>
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-white">Unlock “{series.title}”</h3>
        <p className="mt-1 text-sm text-slate-400">One payment unlocks the whole series — every season and episode — forever, on web and in the app.</p>
        <p className="mt-5 font-heading text-3xl font-black text-white" data-testid="paywall-price">{gbp(access.price)}</p>

        {!user ? (
          <button onClick={() => navigate("/app/login", { state: { from: `/app/series/${series.id}` } })} data-testid="paywall-signin" className="mt-5 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover">
            Sign in to buy
          </button>
        ) : err === "insufficient" || insufficient ? (
          <div className="mt-5">
            <p className="text-sm font-semibold text-crimson" data-testid="paywall-insufficient">
              Not enough in your wallet for {gbp(access.price)}.
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Your wallet: {balance !== null ? gbp(balance) : "…"}</p>
            <button onClick={deposit} disabled={busy} data-testid="paywall-deposit" className="mt-3 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
              {busy ? "Redirecting…" : "Deposit"}
            </button>
          </div>
        ) : (
          <>
            {err && <p className="mt-4 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-crimson" data-testid="paywall-error">{err}</p>}
            <button onClick={buy} disabled={busy} data-testid="paywall-pay" className="mt-5 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
              {busy ? "Processing…" : `Pay ${gbp(access.price)} from wallet`}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// "Support" button + tip panel for videos, shorts and series episodes.
export const SupportButton = ({ target, creator, compact }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!creator || (user && creator.id === user.id)) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`support-${target.id}`} className={compact ? "flex flex-col items-center gap-1 text-white" : "inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-200 transition-colors hover:border-crimson/50"}>
        <Gift size={compact ? 22 : 14} className="text-crimson" />
        {compact ? <span className="font-mono text-[10px]">Support</span> : "Support"}
      </button>
      {open && <TipModal target={target} creator={creator} onClose={() => setOpen(false)} />}
    </>
  );
};

const QUICK = [2, 5, 10, 20];

const TipModal = ({ target, creator, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (user) localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
  }, [user]);

  const val = parseFloat(amount) || 0;
  const outOfRange = val > 0 && (val < 1 || val > 500);
  const insufficient = balance !== null && val > 0 && balance < val - 1e-9;

  const send = async () => {
    if (!val || val <= 0) { setErr("Enter an amount."); return; }
    if (outOfRange) { setErr("Tips must be between £1 and £500."); return; }
    setBusy(true);
    setErr("");
    try {
      await localApi("POST", "/tips", { amount: val, target_id: target.id, target_title: target.title, creator_id: creator.id }, getToken());
      setDone(true);
    } catch (e) {
      if (/insufficient/i.test(e.message)) {
        localApi("GET", "/wallet", undefined, getToken()).then((w) => setBalance(w.balance)).catch(() => {});
        setErr("insufficient");
      } else setErr(e.message || "Tip failed — no money was taken.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="tip-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-crimson/15 text-crimson"><Gift size={20} /></span>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="tip-close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="py-6 text-center">
            <p className="font-heading text-lg font-bold text-emerald-400" data-testid="tip-sent">Tip sent</p>
            <p className="mt-1 text-sm text-slate-400">Thank you for supporting {creator.displayName || creator.username}.</p>
            <button onClick={onClose} className="mt-5 rounded-full border border-white/15 px-6 py-2.5 font-heading text-xs font-bold text-slate-200 hover:border-white/40" data-testid="tip-done-close">Close</button>
          </div>
        ) : (
          <>
            <h3 className="mt-4 font-heading text-lg font-bold text-white" data-testid="tip-title">Support {creator.displayName || creator.username}</h3>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-500" data-testid="tip-wallet-balance">
              Your wallet: {balance !== null ? gbp(balance) : "…"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {QUICK.map((a) => (
                <button key={a} onClick={() => setAmount(String(a))} data-testid={`tip-quick-${a}`} className={`rounded-full border px-4 py-2 font-heading text-sm font-bold transition-colors ${val === a ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-300 hover:border-crimson/50"}`}>
                  £{a}
                </button>
              ))}
            </div>
            <div className="relative mt-3">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
              <input type="number" min="1" max="500" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Other amount (£1 – £500)" data-testid="tip-amount" className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60" />
            </div>
            {err === "insufficient" || insufficient ? (
              <div className="mt-4">
                <p className="text-sm font-semibold text-crimson" data-testid="tip-insufficient">
                  Not enough in your wallet for {gbp(val)}.
                </p>
                <button onClick={() => navigate("/app/wallet")} data-testid="tip-deposit" className="mt-3 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white hover:bg-crimson-hover">Deposit</button>
              </div>
            ) : (
              <>
                {err && <p className="mt-3 text-sm font-semibold text-crimson" data-testid="tip-error">{err}</p>}
                <button onClick={send} disabled={busy || !val || outOfRange} data-testid="tip-send" className="mt-4 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50">
                  {busy ? "Sending…" : `Send ${val > 0 ? gbp(val) : "tip"}`}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
